import { downloadPapers, ExportParams } from '../utils/export';
import { events } from '../analytics/events';
import { ExportFormat } from '../utils/paper-formatting';
import { useTrackedHandler } from './useTracking';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import capitalize from 'lodash-es/capitalize';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import styled from 'styled-components';

export type ExportDialogActivator = (params: ExportParams) => void;

interface ExportDialogResult {
  DownloadDialog: () => JSX.Element;
  openDownloadDialog: ExportDialogActivator;
}

export const useExportDialog = (): ExportDialogResult => {
  const [open, setOpen] = useState(false);
  const paramsRef = useRef<ExportParams | undefined>(undefined);

  const openDownloadDialog = useCallback((params: ExportParams): void => {
    paramsRef.current = params;
    setOpen(true);
  }, []);

  const handleClose = useTrackedHandler(
    events.export.ExportDialogCancelClicked,
    () => {
      setOpen(false);
    },
  );

  const handleConfirm = useTrackedHandler(
    events.export.ExportDialogExportClicked,
    (_: ExportFormat) => {
      setOpen(false);
    },
    (formatType) => ({ formatType }),
  );

  function DownloadDialog(): JSX.Element {
    return (
      <ExportDialog open={open} onClose={handleClose}>
        {paramsRef.current && (
          <ExportDialogContent
            exportParams={paramsRef.current}
            onClose={handleClose}
            onConfirm={handleConfirm}
          />
        )}
      </ExportDialog>
    );
  }

  return { DownloadDialog, openDownloadDialog };
};

// Separate component for the dialog content to avoid unnecessary re-renders
interface DownloadDialogContentProps {
  exportParams: ExportParams;
  onClose: () => void;
  onConfirm: (formatType: ExportFormat) => void;
}

function ExportDialogContent(props: DownloadDialogContentProps): JSX.Element {
  const { exportParams, onClose, onConfirm } = props;

  const [formatType, setFormatType] = useState<ExportFormat>('bibtex');

  const handleConfirm = (): void => {
    onConfirm(formatType);
    downloadPapers(exportParams, formatType);
  };

  const handleFormatChange = useTrackedHandler(
    events.export.ExportDialogFormatChanged,
    (event: SelectChangeEvent<ExportFormat>) => {
      setFormatType(event.target.value as ExportFormat);
    },
    (event) => ({ formatType: event.target.value as ExportFormat }),
  );

  const citations = exportParams.papers.length === 1 ? 'citation' : 'citations';

  const titleText =
    exportParams.type === 'export-all'
      ? `Export ${exportParams.papers.length.toLocaleString()} ${capitalize(
          citations,
        )}`
      : `Export ${exportParams.papers.length.toLocaleString()} Selected ${capitalize(
          citations,
        )}`;

  const explainText = `Download ${citations} for query "${exportParams.query}"`;

  return (
    <>
      <StyledDialogTitle id="alert-dialog-title">{titleText}</StyledDialogTitle>
      <StyledDialogContent>
        <ExplainText>{explainText}</ExplainText>
        <FormatDiv>
          <FormatLabel>Format Type:</FormatLabel>
          <Select
            value={formatType}
            size="small"
            variant="outlined"
            onChange={handleFormatChange}
          >
            <MenuItem value="bibtex">BibTeX</MenuItem>
            <MenuItem value="json">JSON</MenuItem>
            <MenuItem value="markdown">Markdown</MenuItem>
          </Select>
        </FormatDiv>
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <DownloadButton onClick={handleConfirm}>
          Download
          <FileDownloadIcon />
        </DownloadButton>
      </StyledDialogActions>
    </>
  );
}

const ExportDialog = styled(Dialog)`
  div .MuiPaper-root {
    background-color: rgb(12, 42, 43);
    max-width: 700px;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  &.MuiTypography-root.MuiDialogTitle-root {
    font-family: Manrope;
    font-weight: 500px;
    font-size: 24px;
    color: rgb(250, 242, 233);
    user-select: none;
    padding-top: 24px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 15px;
`;

const ExplainText = styled(Typography)`
  font-family: Manrope;
  font-wight: 400;
  font-size: 16px;
  color: rgb(250, 242, 233);
  user-select: none;
`;

const FormatDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-items: start;
  gap: 10px;
`;

const FormatLabel = styled(Typography)`
  font-family: Manrope;
  font-wight: 400;
  font-size: 16px;
  color: rgb(250, 242, 233);
  user-select: none;
`;

const StyledDialogActions = styled(DialogActions)`
  &.MuiDialogActions-root {
    height: 74px;
    padding: 24px 18px;
    border-top: 1px solid rgba(250, 242, 233, 0.1);
  }
`;

const DownloadButton = styled(Button).attrs({
  variant: 'contained',
})`
  &.MuiButtonBase-root.MuiButton-root {
    font-size: 14px;
    font-weight: 400;
    color: rgb(10, 50, 53);
    font-weight: 400;
    background-color: rgb(15, 203, 140);
    margin: 0;
    padding: 6px 20px;
  }

  &.MuiButtonBase-root.MuiButton-root .MuiSvgIcon-root {
    width: 14px;
    height: 14px;
    margin-left: 8px;
  }
`;

const CancelButton = styled(Button)`
  &.MuiButtonBase-root.MuiButton-root {
    font-size: 14px;
    font-weight: 400;
    color: rgb(250, 242, 233);
    padding: 4px;
    margin: 2px 16px 0px 16px;
  }
`;
